import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useFormik } from "formik";
import * as Yup from "yup";
import grlogo from "assets/logo/gr-logo.png";
import axiosServices from "utils/axios";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { msalInstance, ensureMsalInitialized } from "auth/msalInstance";
import { loginRequest } from "auth/msalConfig";
import { enterpriseTokens } from "styles/theme";

const fetchPostData = async (object) => {
    const params = new URLSearchParams();
    Object.keys(object).forEach((key) => params.append(key, object[key]));

    const response = await axiosServices.post("Account/login", params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return response.data;
};

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        height: "48px",
        borderRadius: "8px",
        backgroundColor: "#F8FAFC",
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Inter, Outfit, sans-serif",
        "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #F8FAFC inset !important",
            WebkitTextFillColor: `${enterpriseTokens.navy} !important`,
        },
    },
};

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const redirectPath = sessionStorage.getItem("postLoginRedirect");
        if (redirectPath) {
            sessionStorage.removeItem("postLoginRedirect");
            navigate(redirectPath, { replace: true });
        } else {
            navigate("/portfolio", { replace: true });
        }
    }, [navigate]);

    const openNotification = (msg, type = "error", duration = 50000) => {
        dispatch(openSnackbar({
            open: true,
            message: msg,
            variant: "alert",
            alert: { color: type, variant: "filled" },
            transition: "SlideUp",
        }));
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const startSso = async () => {
        setSsoLoading(true);
        try {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            sessionStorage.clear();
            await ensureMsalInitialized();
            await msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            console.error("SSO start failed", error);
            openNotification("Failed to start SSO. See console for details.");
            setSsoLoading(false);
        }
    };

    const formik = useFormik({
        initialValues: { email: "", password: "" },
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email format").required("Email is required"),
            password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const response = await fetchPostData({
                    email: values.email.trim(),
                    password: values.password.trim(),
                    grant_type: "password",
                });

                if (response?.data?.otp) {
                    openNotification(`OTP sent successfully to your email: ${response.data.otp}`, "success");
                    navigate("/verify-otp", {
                        state: { email: values.email.trim(), password: values.password.trim() },
                    });
                } else if (response?.data?.access_token) {
                    localStorage.setItem("token", response.data.access_token);
                    navigate("/dashboard");
                } else {
                    openNotification(response?.data?.message || "Login failed");
                }
            } catch (error) {
                openNotification(error?.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#EEF3F8",
                p: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 480 }}>
                <Box
                    component="form"
                    onSubmit={formik.handleSubmit}
                    sx={{
                        bgcolor: "#FFFFFF",
                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                        borderRadius: "12px",
                        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.10)",
                        p: { xs: 3, sm: 4 },
                    }}
                >
                    <Typography sx={{ textAlign: "center", fontSize: 30, lineHeight: "38px", fontWeight: 850, color: enterpriseTokens.navy }}>
                        Sign in
                    </Typography>
                    <Typography sx={{ textAlign: "center", mt: 0.75, mb: 3.5, fontSize: 14, color: enterpriseTokens.muted }}>
                        Access your enterprise project workspace
                    </Typography>

                    <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 800, color: enterpriseTokens.slate }}>
                        Work email
                    </Typography>
                    <TextField
                        placeholder="name@company.com"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        fullWidth
                        autoComplete="email"
                        sx={fieldSx}
                    />

                    <Typography sx={{ mt: 2.5, mb: 0.75, fontSize: 13, fontWeight: 800, color: enterpriseTokens.slate }}>
                        Password
                    </Typography>
                    <TextField
                        placeholder="Enter your password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        fullWidth
                        autoComplete="current-password"
                        sx={fieldSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword((value) => !value)} edge="end" aria-label="Toggle password visibility">
                                        {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                        <Typography component={Link} to="/forgot-password" sx={{ fontSize: 13, fontWeight: 750, color: enterpriseTokens.primary, textDecoration: "none" }}>
                            Forgot password?
                        </Typography>
                    </Box>

                    <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mt: 2.5, height: 48, fontSize: 15, fontWeight: 800 }}>
                        {loading ? <><CircularProgress size={19} sx={{ color: "inherit", mr: 1 }} />Signing in...</> : "Sign in"}
                    </Button>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2.5 }}>
                        <Box sx={{ flex: 1, borderTop: `1px solid ${enterpriseTokens.line}` }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: enterpriseTokens.muted }}>OR</Typography>
                        <Box sx={{ flex: 1, borderTop: `1px solid ${enterpriseTokens.line}` }} />
                    </Box>

                    <Button onClick={startSso} disabled={ssoLoading} variant="outlined" fullWidth sx={{ height: 48, color: enterpriseTokens.navy, bgcolor: "#FFFFFF" }}>
                        <img src={grlogo} alt="" style={{ width: 23, height: 23, objectFit: "contain", marginRight: 10 }} />
                        {ssoLoading ? "Starting SSO..." : "Sign in using SSO"}
                    </Button>

                    <Typography sx={{ mt: 3, textAlign: "center", fontSize: 11.5, lineHeight: "18px", color: enterpriseTokens.muted }}>
                        By continuing, you agree to the Terms of Service and acknowledge the Global Privacy Statement.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;
